import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';

export interface ProfitMarginFilterValue {
  filter: string;
  filterType: string;
  value?: string;
}

@Component({
  selector: 'app-profit-margin-filter',
  templateUrl: './app-profit-margin-filter.component.html',
  styleUrls: ['./app-profit-margin-filter.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppProfitMarginFilterComponent),
      multi: true,
    },
  ],
})
export class AppProfitMarginFilterComponent implements ControlValueAccessor {
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;

  @Input() labelText: string = 'Profit margin';
  @Input() label: string = '';

  get resolvedLabel(): string {
    return this.label || this.labelText || 'Profit margin';
  }
  @Input() disabled: boolean = false;

  @Output() filterChange = new EventEmitter<ProfitMarginFilterValue | null>();
  @Output() clear = new EventEmitter<void>();

  isFilterApplied: boolean = false;
  selectedEquality: string = 'gt';
  filterValue: string = '';
  selectedView: string = '';

  equalityOptions = [
    { label: 'Is Greater Than', value: 'gt' },
    { label: 'Is Greater Than Equal To', value: 'gte' },
    { label: 'Is Equal To', value: 'eq' },
    { label: 'Is Lesser Than', value: 'lt' },
    { label: 'Is Lesser Than Equal To', value: 'lte' },
  ];

  private onChange: (val: ProfitMarginFilterValue | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  writeValue(val: ProfitMarginFilterValue | null): void {
    if (val && (val.value || val.filterType)) {
      this.isFilterApplied = true;
      this.selectedEquality = val.filter || 'gt';
      this.filterValue = val.value || val.filterType || '';
      this.selectedView = this.filterValue;
    } else {
      this.isFilterApplied = false;
      this.selectedEquality = 'gt';
      this.filterValue = '';
      this.selectedView = '';
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  openDefault(): void {
    if (!this.selectedEquality) {
      this.selectedEquality = 'gt';
    }
  }

  prevData(): void {
    // Keep current state when reopened
  }

  validateNumberInput(event: any): void {
    const inputElement = event.target as HTMLInputElement;
    let inputValue = inputElement.value;

    inputValue = inputValue.replace(/[^\d.]/g, '');
    const parts = inputValue.split('.');
    if (parts.length > 2) {
      inputValue = parts[0] + '.' + parts.slice(1).join('');
    }
    if (parts[1] && parts[1].length > 2) {
      inputValue = parts[0] + '.' + parts[1].slice(0, 2);
    }

    this.filterValue = inputValue;
    inputElement.value = inputValue;
  }

  applyFilter(): void {
    if (!this.filterValue || this.filterValue.trim() === '') {
      return;
    }

    this.isFilterApplied = true;
    this.selectedView = this.filterValue;

    const payload: ProfitMarginFilterValue = {
      filter: this.selectedEquality,
      filterType: this.filterValue,
      value: this.filterValue,
    };

    this.onChange(payload);
    this.filterChange.emit(payload);

    if (this.menuTrigger) {
      this.menuTrigger.closeMenu();
    }
    this.cdr.markForCheck();
  }

  removeFilter(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isFilterApplied = false;
    this.filterValue = '';
    this.selectedView = '';
    this.selectedEquality = 'gt';

    this.onChange(null);
    this.filterChange.emit(null);
    this.clear.emit();

    if (this.menuTrigger) {
      this.menuTrigger.closeMenu();
    }
    this.cdr.markForCheck();
  }
}
